import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Icon,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Image,
  useColorModeValue,
  Divider,
  HStack,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { FaBell, FaLock, FaPlay, FaCode, FaRocket, FaUserPlus } from "react-icons/fa";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../brandConfig";
import { UnifiedLoginModal } from "./authentication";
import { CaptureUserDetailsModal } from "./authentication/components/CaptureUserDetailsModal";
import { useAuth } from "../contexts/AuthContext";

const Podcast: React.FC = () => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCaptureDetailsModal, setShowCaptureDetailsModal] = useState(false);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);

  // Check if user has completed profile using user data from AuthContext
  const hasCompletedProfile = user && 
    user.email && 
    user.email.trim() !== '' &&
    user.fName &&
    user.lName;

  // Debug logging
  useEffect(() => {
    console.log('🖔 Auth loading?', authLoading);
    console.log('🔑 Authenticated?', isAuthenticated);
    if (user) {
      console.log('🔍 User data from AuthContext:', user);
      console.log('📧 Has email?', !!user?.email);
      console.log('👤 Has name?', !!user?.fName, !!user?.lName);
      console.log('✅ Profile complete?', hasCompletedProfile);
    } else {
      console.log('⚠️ No user data available yet');
    }
  }, [user, hasCompletedProfile, authLoading, isAuthenticated]);

  useEffect(() => {
    // Wait for auth to finish loading before checking profile
    if (isAuthenticated && !authLoading && !hasCheckedProfile) {
      setHasCheckedProfile(true);
      
      // Check if profile is complete using user data
      if (user && !hasCompletedProfile) {
        console.log('🚫 Showing profile modal - profile incomplete');
        console.log('User data:', user);
        setShowCaptureDetailsModal(true);
      } else if (hasCompletedProfile) {
        console.log('✅ Profile is complete, not showing modal');
        setShowCaptureDetailsModal(false);
      } else if (!user) {
        console.log('⏳ Waiting for user data to load...');
      }
    }
  }, [isAuthenticated, hasCompletedProfile, authLoading, user, hasCheckedProfile]);

  const handleDetailsCapture = () => {
    setShowCaptureDetailsModal(false);
    setHasCheckedProfile(false); // Reset so we can check again
    // Auth context will automatically refresh
  };

  const handleSignUpClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
    } else if (!hasCompletedProfile) {
      setShowCaptureDetailsModal(true);
    }
  };

  // Gradient background for cards
  const cardGradientBg = `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder = "rgba(255, 255, 255, 0.1)";
  const textPrimary = "#FFFFFF";
  const textSecondary = "rgba(255, 255, 255, 0.7)";
  const textMuted = "rgba(255, 255, 255, 0.5)";
  const accentBlue = "#3b82f6";

  return (
    <Box minH="100vh" bg={getColor("background.main")} display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      
      <Container maxW="7xl" py={16} flex="1">
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge
              bg={`${accentBlue}20`}
              color={accentBlue}
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="full"
              fontWeight="500"
              border="1px solid"
              borderColor={`${accentBlue}40`}
              mb={4}
            >
              Exclusive Updates
            </Badge>
            <Heading
              as="h1"
              size="2xl"
              mb={4}
              color={textPrimary}
              fontFamily={getBrandValue("fonts.heading")}
            >
              Block52 Technical Updates
            </Heading>
            <Text
              fontSize="xl"
              color={textSecondary}
              maxW="800px"
              mx="auto"
              mb={8}
            >
              Sign up for the latest technical updates on Block52 projects: Poker Virtual Machine (PVM), Club Management System, and decentralized poker infrastructure
            </Text>
          </Box>

          {/* Authentication Gate - Show loading while checking profile */}
          {authLoading && isAuthenticated ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color={accentBlue} thickness="4px" />
                <Text color={textSecondary}>Loading your profile...</Text>
              </VStack>
            </Center>
          ) : !isAuthenticated || (!authLoading && !hasCompletedProfile) ? (
            <Card
              bg={cardGradientBg}
              backdropFilter="blur(10px)"
              boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={cardBorder}
              overflow="hidden"
            >
              <CardBody p={12}>
                <VStack spacing={8}>
                  <Icon as={FaLock} w={20} h={20} color={accentBlue} />

                  <VStack spacing={4}>
                    <Heading size="lg" color={textPrimary}>
                      Sign Up for Block52 Technical Updates
                    </Heading>
                    <Text fontSize="lg" color={textSecondary} textAlign="center" maxW="600px">
                      Join our community to receive exclusive updates on Block52 projects,
                      technical developments, and the future of decentralized poker.
                    </Text>
                  </VStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full" maxW="600px">
                    <VStack>
                      <Icon as={FaCode} w={10} h={10} color={accentBlue} />
                      <Text fontWeight="bold" color={textPrimary}>Project Updates</Text>
                      <Text fontSize="sm" color={textMuted} textAlign="center">
                        Latest PVM and infrastructure news
                      </Text>
                    </VStack>
                    <VStack>
                      <Icon as={FaBell} w={10} h={10} color={accentBlue} />
                      <Text fontWeight="bold" color={textPrimary}>Weekly Releases</Text>
                      <Text fontSize="sm" color={textMuted} textAlign="center">
                        New features and improvements
                      </Text>
                    </VStack>
                    <VStack>
                      <Icon as={FaRocket} w={10} h={10} color={accentBlue} />
                      <Text fontWeight="bold" color={textPrimary}>Early Access</Text>
                      <Text fontSize="sm" color={textMuted} textAlign="center">
                        Beta features and testing opportunities
                      </Text>
                    </VStack>
                  </SimpleGrid>

                  <Button
                    size="lg"
                    bg={accentBlue}
                    color="white"
                    leftIcon={<Icon as={FaUserPlus} />}
                    _hover={{
                      bg: "#2563eb",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    onClick={handleSignUpClick}
                    px={8}
                    py={6}
                    fontSize="lg"
                  >
                    Sign Up for Free Access
                  </Button>

                  <Text fontSize="sm" color={textMuted}>
                    No credit card required • Instant access • Cancel anytime
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            /* Authenticated Content */
            <>
              <Alert
                status="success"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="200px"
                borderRadius="xl"
                bg={`${accentBlue}10`}
                borderWidth="1px"
                borderColor={`${accentBlue}40`}
              >
                <AlertIcon boxSize="40px" mr={0} color={accentBlue} />
                <AlertTitle mt={4} mb={1} fontSize="lg" color={textPrimary}>
                  Welcome {user?.fName || 'back'}!
                </AlertTitle>
                <AlertDescription maxWidth="sm" color={textSecondary}>
                  You're now subscribed to Block52 technical updates.
                </AlertDescription>
              </Alert>

              {/* Update 1: PVM Alpha Release */}
              <Card
                bg={cardGradientBg}
                backdropFilter="blur(10px)"
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                borderRadius="xl"
                borderWidth="1px"
                borderColor={cardBorder}
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  borderColor: `${accentBlue}40`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px 0 rgba(59, 130, 246, 0.2)"
                }}
              >
                <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={2}>
                      <Badge colorScheme="blue">Update #1</Badge>
                      <Heading size="lg" color={textPrimary}>
                        Poker Virtual Machine (PVM) Alpha Release
                      </Heading>
                      <Text color={textSecondary}>
                        Status: In Development • Expected: Q1 2025
                      </Text>
                    </VStack>
                    <Icon as={FaCode} w={12} h={12} color={accentBlue} />
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="lg" color={textSecondary}>
                      The Poker Virtual Machine (PVM) alpha is approaching completion. This groundbreaking
                      infrastructure will power the next generation of decentralized poker clubs with
                      transparent operations, advanced anti-collusion systems, and seamless blockchain integration.
                    </Text>

                    <Box>
                      <Text fontWeight="bold" color={textPrimary} mb={2}>
                        Key Features:
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <HStack>
                          <Box w="2" h="2" bg={accentBlue} borderRadius="full" />
                          <Text color={textSecondary}>Decentralized game state management</Text>
                        </HStack>
                        <HStack>
                          <Box w="2" h="2" bg={accentBlue} borderRadius="full" />
                          <Text color={textSecondary}>Smart contract-based rake distribution</Text>
                        </HStack>
                        <HStack>
                          <Box w="2" h="2" bg={accentBlue} borderRadius="full" />
                          <Text color={textSecondary}>AI-powered anti-collusion detection</Text>
                        </HStack>
                        <HStack>
                          <Box w="2" h="2" bg={accentBlue} borderRadius="full" />
                          <Text color={textSecondary}>Multi-chain validator node network</Text>
                        </HStack>
                      </SimpleGrid>
                    </Box>

                    <Divider borderColor={cardBorder} />

                    {/* Update Placeholder */}
                    <Box
                      bg="rgba(59, 130, 246, 0.1)"
                      backdropFilter="blur(10px)"
                      borderWidth="1px"
                      borderColor="rgba(59, 130, 246, 0.3)"
                      p={8}
                      borderRadius="xl"
                      textAlign="center"
                    >
                      <VStack spacing={4}>
                        <Icon as={FaRocket} w={16} h={16} color={accentBlue} />
                        <Text fontWeight="bold" fontSize="xl" color={textPrimary}>
                          Development In Progress
                        </Text>
                        <Text color={textSecondary}>
                          We're working hard to bring the PVM to life.
                          You'll receive detailed technical updates as we progress.
                        </Text>
                        <Badge
                          bg={`${accentBlue}20`}
                          color={accentBlue}
                          fontSize="sm"
                          px={4}
                          py={2}
                        >
                          Next Update: Early January 2025
                        </Badge>
                      </VStack>
                    </Box>

                    <Alert status="info" borderRadius="lg" bg="rgba(59, 130, 246, 0.1)" borderColor="rgba(59, 130, 246, 0.3)">
                      <AlertIcon color="#3B82F6" />
                      <Box>
                        <AlertTitle color={textPrimary}>You're subscribed!</AlertTitle>
                        <AlertDescription color={textSecondary}>
                          We'll send you email and SMS notifications with each major project milestone.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  </VStack>
                </CardBody>
              </Card>

              {/* Future Updates Preview */}
              <Box>
                <Heading size="lg" mb={6} color={textPrimary}>
                  Upcoming Project Updates
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Card
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    opacity={0.7}
                  >
                    <CardBody>
                      <Badge colorScheme="gray" mb={2}>Update #2</Badge>
                      <Heading size="md" color={textPrimary} mb={2}>
                        Club-in-a-Box Platform Launch
                      </Heading>
                      <Text color={textMuted}>
                        Complete turnkey solution for launching your own poker club on the blockchain with full infrastructure support.
                      </Text>
                    </CardBody>
                  </Card>

                  <Card
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    opacity={0.7}
                  >
                    <CardBody>
                      <Badge colorScheme="gray" mb={2}>Update #3</Badge>
                      <Heading size="md" color={textPrimary} mb={2}>
                        Validator Node Network Expansion
                      </Heading>
                      <Text color={textMuted}>
                        Growing the decentralized validator network with new node operators and enhanced staking mechanisms.
                      </Text>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </Box>
            </>
          )}
        </VStack>
      </Container>

      <FooterWithFourColumns />

      {/* Modals */}
      <UnifiedLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          // After SMS login, show capture details modal
          if (!hasCompletedProfile) {
            setShowCaptureDetailsModal(true);
          }
        }}
      />

      <CaptureUserDetailsModal
        isOpen={showCaptureDetailsModal}
        onClose={() => setShowCaptureDetailsModal(false)}
        userId={user?.id || ""}
        onUpdateSuccess={handleDetailsCapture}
        currentUserData={{
          fName: user?.fName || "",
          lName: user?.lName || "",
          email: user?.email || ""
        }}
      />
    </Box>
  );
};

export default Podcast;